import { useApplyEdition } from '@/pages/Apply/components/use-apply-edition';
import { cn } from '@/lib/utils';
import type { ApplyInvitesShow } from '@/types';
import { EventLabel } from '../components/EventLabel';
import { GithubSignInButton } from '../components/GithubSignInButton';
import { InviteNotFound } from '../components/InviteNotFound';

// app/(public)/${eventSlug}/invite/[handle]/page.tsx + invite-landing.tsx.
export default function Show({
  segment,
  leader,
  placeholderName,
  teamSlots,
  notFoundReason,
}: ApplyInvitesShow) {
  const { eventSlug } = useApplyEdition();
  if (notFoundReason || !leader) {
    return (
      <>
        <InviteNotFound reason={notFoundReason ?? 'invalid'} />
      </>
    );
  }

  return (
    <>
      <div className="mx-auto max-w-xl space-y-8 px-4 py-12">
        <header className="space-y-3 text-center">
          <p className="text-muted-foreground text-sm">
            Postulación a <EventLabel />
          </p>
          <h1 className="font-bold text-2xl uppercase tracking-wide sm:text-3xl">
            @{leader.githubHandle} te invitó a su equipo
          </h1>
          <p className="text-muted-foreground">
            Si eres <strong>{placeholderName}</strong>, conecta tu cuenta de
            GitHub para sumarte.
          </p>
        </header>

        <section className="space-y-3">
          <h2 className="font-bold text-sm uppercase tracking-wide">
            Tu equipo ({teamSlots.length + 1} personas)
          </h2>
          <div className="space-y-2">
            <TeamMemberRow
              name={leader.fullName}
              handle={leader.githubHandle}
              label="Líder"
              highlight={false}
            />
            {teamSlots.map((s) => (
              <TeamMemberRow
                key={s.id}
                name={s.claimedFullName ?? s.placeholderName}
                handle={s.claimedGithubHandle ?? undefined}
                label={
                  s.status === 'completed'
                    ? 'Listo'
                    : s.status === 'in_progress'
                      ? 'En proceso'
                      : 'Pendiente'
                }
                highlight={s.isThisOne}
              />
            ))}
          </div>
        </section>

        <div className="border-2 border-primary bg-background/80 p-6">
          <div className="space-y-4">
            <p className="text-sm">
              Para sumarte, conecta tu cuenta de GitHub.
            </p>
            <GithubSignInButton
              callbackURL={`/${eventSlug}/invite/${segment}/identity`}
              errorCallbackURL={`/${eventSlug}/invite/${segment}?error=github_signin_failed`}
              label="Iniciar con GitHub"
            />
          </div>
        </div>
      </div>
    </>
  );
}

function TeamMemberRow({
  name,
  handle,
  label,
  highlight,
}: {
  name: string;
  handle?: string;
  label: string;
  highlight: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 border-2 p-3',
        highlight
          ? 'border-primary bg-primary/10'
          : 'border-input bg-background/40',
      )}
    >
      <div>
        <p className="font-bold">{name}</p>
        {handle && <p className="text-muted-foreground text-xs">@{handle}</p>}
      </div>
      <span className="border border-input px-2 py-0.5 text-muted-foreground text-xs uppercase tracking-wide">
        {label}
      </span>
    </div>
  );
}
